import { toFirstUpperCase } from 'infrastructure/functions';

import {
  factorsBlurb,
  medicationBlurb,
  nursingInterventionBlurb,
  Q1Blurb,
  Q2Blurb,
  Q3Blurb,
  Q4Blurb,
  symptomsBlurb,
  verbalizedBlurb,
} from './common';

type TData = {
  [k: string]: Record<string, any>;
};

export const generateChfWeightMessage = <T extends TData>(data: T) => {
  const { Q1, Q2, Q3, Q4, Q5, Q6, Q7, Q8, Q9, Q10 } = data;
  let message = '';

  const relationship = Q1?.relationship
    ? toFirstUpperCase(Q1.relationship)
    : '';
  const speakWith = Q1.speakWithPatient
    ? 'Patient'
    : `${Q1.patient ?? ''}${relationship}`;
  const thirdPersonText = Q1.speakWithPatient ? '' : ' states that patient';
  const speakerStatement = speakWith + thirdPersonText;

  // Q1
  if (Q1) {
    message += Q1Blurb({
      reading: Q1.reading,
      dateOfReading: Q1.dateOfReading,
      patient: Q1.patient,
      speakWithOther: Q1.speakWithOther,
      relationship: Q1.relationship,
      readingName: 'weight',
    });
    message += '\n';
  }

  // Q2
  if (Q2.AOC) {
    message += Q2Blurb(speakerStatement, Q2.AOC);
    message += '\n';
  }

  // Q3
  if (Q3.last || Q3.next) {
    message += Q3Blurb(speakerStatement, Q3.next, Q3.last);
    message += '\n';
  }

  // Q4
  message += Q4Blurb(speakerStatement, {
    isOneMoreReading: Q4.isOneMoreReading,
    bounds: Q4.bounds,
    repeatReadingWas: Q4.repeatReadingWas,
  });
  message += '\n';

  // Q5
  message += symptomsBlurb({
    speakerStatement,
    emergencyModalAnswer: Q5.emergencyModalAnswer,
    confirmedSymptoms: Q5.confirmedSymptoms,
    deniedSymptoms: Q5.deniedSymptoms,
    confirmedEmergentSymptoms: Q5.confirmedEmergentSymptoms,
    symptomsOnset: Q5.symptomsOnset,
    isAllSymptomsDenied: Q5.isAllSymptomsDenied,
    speakWith,
    speakWithPatient: Q1.speakWithPatient,
  });
  message += '\n';

  // Q6
  if (Q6.isTakeMedications !== null) {
    message += medicationBlurb({
      isTakeMedications: Q6.isTakeMedications,
      speakerStatement,
      speakWithPatient: Q1?.speakWithPatient,
      type: 'routine',
      medications: Q6.medications,
    });
    message += '\n';
  }

  // Q7
  if (Q7.isTakePrnMedications !== null) {
    message += medicationBlurb({
      isTakeMedications: Q7.isTakePrnMedications,
      speakerStatement,
      speakWithPatient: Q1?.speakWithPatient,
      type: 'any PRN',
      medications: Q7.prnMedications,
    });
    message += '\n';
  }

  // Q8
  if (Q8.confirmedFactors?.length > 0 || Q8.deniedFactors?.length > 0) {
    message += factorsBlurb({
      speakerStatement,
      confirmedFactors: Q8.confirmedFactors,
      deniedFactors: Q8.deniedFactors,
      readingName: 'weight',
    });
    message += '\n';
  }

  // Q9
  message += nursingInterventionBlurb({
    escalationToClinic: Q9.escalationToClinic,
    notEscalated: Q9.notEscalated,
    interventionDetails: Q9.interventionDetails,
    emergencyRoom: Q9.emergencyRoom,
    escalation: Q9.escalation,
    nursingEducation: Q9.nursingEducation,
  });
  message += '\n';

  // Q10
  if (Q10.verbalized !== null) {
    message += verbalizedBlurb(speakerStatement, Q10.verbalized);
    message += '\n';
  }

  return message.trim();
};
